import type { ReactNode, FC } from 'react';
import c from 'classnames';
import { useSelector } from 'react-redux';
import { Url } from '@wix/da-url';
import { BiData, OpenUploadBiEvent } from '@wix/da-bi/pkg/events';
import UploadItemButton from '@wix/da-shared-react/pkg/UploadItemButton';
import PlainButtonOrLink from '@wix/da-ds/pkg/Buttons/PlainButtonOrLink';
import { useUnlockVideoUploadsCheck } from '@wix/da-submit-shared/pkg/modals/UnlockWithCoreUpsellModal/useUnlockVideoUploadsCheck';
import { useStudioUploadOptions } from '../../../../hooks/useStudioUploadOptions';
import { illustrationUrl } from '../../StudioBlankSlate';
import { getIsUploadPage } from '../../../../selectors/studio';
import { STUDIO_UPLOADS_NAMESPACE } from '../../../../../constants';

import s from './UploadBlankSlate.scss';

interface Props {
  title: string;
  subtitle?: string;
  ctaLabel?: string;
  illustrationSrc?: string;
  customIllustration?: ReactNode;
  linkToSubmit?: boolean;
  linkToSubmitParams?: any;
  noBorder?: boolean;
}

const UPLOAD_IMG_URL = illustrationUrl('studio-upload-empty.png', 2);
const ILLUSTRATION_HEIGHT = 120;

const UploadBlankSlate: FC<Props> = ({
  title,
  subtitle,
  ctaLabel,
  illustrationSrc,
  customIllustration,
  linkToSubmit,
  linkToSubmitParams = {},
  noBorder,
}) => {
  const isUploadPage = useSelector(getIsUploadPage);
  const { modal: lockedVideoUpsellDialog, ...lockedVideoUploadOptions } =
    useUnlockVideoUploadsCheck();
  const uploadOptions = useStudioUploadOptions({
    allowDragAndDrop: isUploadPage ? true : false,
    ...lockedVideoUploadOptions,
  });

  const uploadButton = (
    <>
      {lockedVideoUpsellDialog}
      <UploadItemButton
        uploadId={STUDIO_UPLOADS_NAMESPACE}
        className={s['upload-button']}
        dropZoneClassName={c(
          s['upload-drop-zone'],
          noBorder && s['no-border']
        )}
        contentClassName={s['upload-content']}
        titleClassName={s['title']}
        subtitleClassName={s['subtitle']}
        uploadOptions={uploadOptions}
        title={title}
        subtitle={subtitle}
        ctaLabel={ctaLabel}
        customIcon={
          customIllustration ?? (
            <img
              className={s['illustration']}
              alt=""
              src={illustrationSrc ?? UPLOAD_IMG_URL}
              height={ILLUSTRATION_HEIGHT}
            />
          )
        }
      />
    </>
  );

  if (linkToSubmit) {
    return (
      <PlainButtonOrLink
        className={s['root']}
        href={Url.studio({
          ...linkToSubmitParams,
          openSubmitForm: true,
        })}
        biData={BiData<OpenUploadBiEvent>({
          evid: 16,
          sectionname: 'table_empty',
        })}
        onClickCapture={ev => {
          ev.preventDefault();
          window.location.href = Url.studio({
            ...linkToSubmitParams,
            openSubmitForm: true,
          });
        }}
      >
        {uploadButton}
      </PlainButtonOrLink>
    );
  }

  return <div className={s['root']}>{uploadButton}</div>;
};
UploadBlankSlate.displayName = 'UploadBlankSlate';

export default UploadBlankSlate;
